/**
 * SingleSelect Component
 *
 * UI/Aesthetics: Single-select dropdown with search functionality
 * Functions:
 * - Single option selection
 * - Search/filter functionality within dropdown
 * - Alphabetical sorting of options
 * - Auto-closes on selection
 * - Keyboard navigation and accessibility
 */

import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import {
  CaretDownIcon,
  XIcon,
  MagnifyingGlassIcon,
  CheckIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { T } from 'gt-react';

export interface SingleSelectOption {
  id: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
  group?: string;
}

interface SingleSelectProps {
  options: SingleSelectOption[];
  value: SingleSelectOption | null;
  onChange: (selected: SingleSelectOption | null) => void;
  /** Applied to the trigger so an external `<label htmlFor>` can name it. The trigger is a
   * `<button>`, which IS a labelable element, so this associates properly — without it a caller's
   * label names nothing and the control is announced by its own contents alone. */
  id?: string;
  label?: string;
  labelPosition?: 'above' | 'left';
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default' | 'small';
  error?: boolean;
  helperText?: string;
  className?: string;
  shouldAutoSort?: boolean;
  allowClear?: boolean;
}

export const SingleSelect: React.FC<SingleSelectProps> = ({
  options,
  value,
  onChange,
  id,
  label,
  labelPosition = 'above',
  placeholder = 'Select an option...',
  searchPlaceholder = 'Search',
  disabled = false,
  fullWidth = false,
  size = 'default',
  error = false,
  helperText,
  className,
  shouldAutoSort = true,
  allowClear = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleOpenChange = (newOpen: boolean) => {
    setSearchTerm('');
    setOpen(newOpen);
  };

  // Convert MUI size to shadcn size
  const getButtonSize = (): 'sm' | 'default' => {
    return size === 'small' ? 'sm' : (size as 'sm' | 'default') || 'default';
  };

  // Sort options alphabetically for consistent dropdown ordering
  const sortedOptions = useMemo(
    () =>
      shouldAutoSort
        ? [...options].sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
          )
        : options,
    [options, shouldAutoSort],
  );

  // Filter options based on search term (from the sorted list)
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return sortedOptions;
    return sortedOptions.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [sortedOptions, searchTerm]);

  const isSelected = (option: SingleSelectOption) => {
    return value?.id === option.id;
  };

  const handleSelect = (option: SingleSelectOption) => {
    // If clicking the already selected option and allowClear is true, deselect it
    if (isSelected(option) && allowClear) {
      onChange(null);
    } else {
      onChange(option);
    }
    setOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  // Grouped options logic
  const groupedOptions = useMemo(() => {
    const groups = new Map<string, SingleSelectOption[]>();
    const ungrouped: SingleSelectOption[] = [];

    filteredOptions.forEach((opt) => {
      if (opt.group) {
        const arr = groups.get(opt.group) ?? [];
        arr.push(opt);
        groups.set(opt.group, arr);
      } else {
        ungrouped.push(opt);
      }
    });

    // Flatten groups with only 1 member
    const flattenedGroups: { group?: string; options: SingleSelectOption[] }[] =
      [];
    for (const [group, opts] of groups.entries()) {
      if (opts.length === 1) {
        flattenedGroups.push({ options: opts });
      } else {
        flattenedGroups.push({ group, options: opts });
      }
    }

    // Combine grouped + ungrouped options
    return [...flattenedGroups, { options: ungrouped }];
  }, [filteredOptions]);

  const labelElement = label ? (
    <Label className={cn('text-sm font-medium', error && 'text-destructive')}>
      {label}
    </Label>
  ) : null;

  return (
    <div
      className={cn(labelPosition === 'above' ? 'space-y-2' : '', className)}
    >
      {labelPosition === 'above' && labelElement}

      {labelPosition === 'left' ? (
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 mt-[2px]">{labelElement}</div>
          <div className="flex-1 flex flex-col">
            <Popover open={open} onOpenChange={handleOpenChange}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  id={id}
                  aria-expanded={open}
                  disabled={disabled}
                  size={getButtonSize()}
                  className={cn(
                    'justify-between font-normal',
                    size === 'sm' && 'h-8',
                    !value && 'text-muted-foreground',
                    fullWidth ? 'w-full' : '',
                    error &&
                      'border-destructive bg-destructive/5 ring-1 ring-destructive/20',
                  )}
                >
                  <span className="truncate">
                    {value ? value.label : placeholder}
                  </span>
                  <div className="flex items-center gap-1">
                    {value && allowClear && (
                      <button
                        type="button"
                        onClick={handleClear}
                        className="p-0.5 hover:bg-accent rounded-sm"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClear(e as unknown as React.MouseEvent);
                          }
                        }}
                      >
                        <XIcon className="h-3 w-3" />
                      </button>
                    )}
                    <CaretDownIcon className="h-4 w-4 opacity-50" />
                  </div>
                </Button>
              </PopoverTrigger>

              <PopoverContent
                className="p-0 z-[9999]"
                style={{
                  minWidth: '200px',
                  maxWidth: 'min(80vw, 800px)',
                }}
                align="start"
              >
                <div className="flex items-center gap-2 p-2 border-b">
                  <MagnifyingGlassIcon className="h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      handleOpenChange(false);
                    }}
                    className="p-1 hover:bg-accent rounded-sm"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </div>

                {/* Options list */}
                <div
                  className="max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
                  onWheel={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#d1d5db #f3f4f6',
                  }}
                >
                  {groupedOptions.some((g) => g.options.length > 0) ? (
                    groupedOptions.map(({ group, options: groupOptions }) => (
                      <div key={group || Math.random()}>
                        {group && groupOptions.length > 1 && (
                          <div className="px-3 py-2 bg-muted/30 border-b text-sm font-medium">
                            {group}
                          </div>
                        )}

                        {groupOptions.map((option) => (
                          <div
                            key={option.id}
                            className={cn(
                              'flex items-center gap-2 px-3 py-2 hover:bg-accent cursor-pointer',
                              group && groupOptions.length > 1 ? 'pl-8' : '',
                              isSelected(option) && 'bg-accent',
                            )}
                            onClick={() => handleSelect(option)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleSelect(option);
                              }
                            }}
                            tabIndex={0}
                            role="option"
                            aria-selected={isSelected(option)}
                          >
                            {isSelected(option) ? (
                              <CheckIcon className="h-4 w-4 text-primary" />
                            ) : (
                              <div className="h-4 w-4" />
                            )}
                            <span className="text-sm flex-1">
                              {option.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-center">
                      <p className="text-sm text-muted-foreground">
                        <T context="dropdown empty state when search yields no results">
                          No options found
                        </T>
                      </p>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {helperText && (
              <p
                className={cn(
                  'text-sm mt-1',
                  error ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                {helperText}
              </p>
            )}
          </div>
        </div>
      ) : (
        <>
          <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                id={id}
                aria-expanded={open}
                disabled={disabled}
                size={getButtonSize()}
                className={cn(
                  'justify-between font-normal',
                  size === 'sm' && 'h-8',
                  !value && 'text-muted-foreground',
                  fullWidth ? 'w-full' : '',
                  error &&
                    'border-destructive bg-destructive/5 ring-1 ring-destructive/20',
                )}
              >
                <span className="truncate">
                  {value ? value.label : placeholder}
                </span>
                <div className="flex items-center gap-1">
                  {value && allowClear && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="p-0.5 hover:bg-accent rounded-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleClear(e as unknown as React.MouseEvent);
                        }
                      }}
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  )}
                  <CaretDownIcon className="h-4 w-4 opacity-50" />
                </div>
              </Button>
            </PopoverTrigger>

            <PopoverContent
              className="p-0 z-[9999]"
              style={{
                minWidth: '300px',
                maxWidth: 'min(80vw, 800px)',
              }}
              align="start"
            >
              <div className="flex items-center gap-2 p-2 border-b">
                <MagnifyingGlassIcon className="h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => {
                    handleOpenChange(false);
                  }}
                  className="p-1 hover:bg-accent rounded-sm"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>

              {/* Options list */}
              <div
                className="max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
                onWheel={(e) => {
                  e.stopPropagation();
                }}
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#d1d5db #f3f4f6',
                }}
              >
                {groupedOptions.some((g) => g.options.length > 0) ? (
                  groupedOptions.map(({ group, options: groupOptions }) => (
                    <div key={group || Math.random()}>
                      {group && groupOptions.length > 1 && (
                        <div className="px-3 py-2 bg-muted/30 border-b text-sm font-medium">
                          {group}
                        </div>
                      )}

                      {groupOptions.map((option) => (
                        <div
                          key={option.id}
                          className={cn(
                            'flex items-center gap-2 px-3 py-2 hover:bg-accent cursor-pointer',
                            group && groupOptions.length > 1 ? 'pl-8' : '',
                            isSelected(option) && 'bg-accent',
                          )}
                          onClick={() => handleSelect(option)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleSelect(option);
                            }
                          }}
                          tabIndex={0}
                          role="option"
                          aria-selected={isSelected(option)}
                        >
                          {isSelected(option) ? (
                            <CheckIcon className="h-4 w-4 text-primary" />
                          ) : (
                            <div className="h-4 w-4" />
                          )}
                          <span className="text-sm flex-1">{option.label}</span>
                        </div>
                      ))}
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">
                      <T context="empty state for single select dropdown">
                        No options found
                      </T>
                    </p>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {helperText && (
            <p
              className={cn(
                'text-sm mt-1',
                error ? 'text-destructive' : 'text-muted-foreground',
              )}
            >
              {helperText}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default SingleSelect;
